import { useState } from "react";
import AuthService from "../../../api/authService";
import { useNotification } from "../../../context/NotificationContext";
import { useLoader } from "../../../context/LoaderContext";
import PseudoField from "../../../components/forms/PseudoField";

const fieldClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-pixy-ink outline-none transition placeholder:text-slate-400 focus:border-pixy-blue focus:ring-2 focus:ring-pixy-blue/20";

export default function RegisterForm({ onSwitch }) {
  const [formData, setFormData] = useState({
    pseudo: "",
    email: "",
    password: "",
    confirmPassword: "",
    acceptTerms: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [suggestedPseudo, setSuggestedPseudo] = useState("");
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errorMessage) {
      setErrorMessage("");
    }
    if (name === "pseudo" && suggestedPseudo) {
      setSuggestedPseudo("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (formData.password !== formData.confirmPassword) {
      const message = "Les mots de passe ne correspondent pas.";
      setErrorMessage(message);
      showNotification("warning", message);
      return;
    }

    showLoader("form-wrapper");

    try {
      const payload = {
        pseudo: formData.pseudo,
        email: formData.email,
        password: formData.password,
      };

      const res = await AuthService.register(payload);
      showNotification("success", res.message || "Votre compte a bien été créé avec succès.");
      onSwitch("login");
    } catch (error) {
      const message =
        error.response?.data?.error || "Une erreur est survenue.";
      const suggestion = error.response?.data?.suggested_pseudo || "";
      setErrorMessage(message);
      setSuggestedPseudo(suggestion);
      showNotification("error", message);
    } finally {
      hideLoader();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="block text-sm font-semibold text-slate-700">
            Pseudo
          </label>
          <PseudoField
            value={formData.pseudo}
            onChange={(value) =>
              handleChange({
                target: { name: "pseudo", value, type: "text" },
              })
            }
            label=""
            placeholder="Ton pseudo"
            inputClassName={fieldClassName}
            helperClassName="text-xs"
          />
          {suggestedPseudo ? (
            <button
              type="button"
              onClick={() =>
                handleChange({
                  target: {
                    name: "pseudo",
                    value: suggestedPseudo,
                    type: "text",
                  },
                })
              }
              className="mt-2 rounded-full border border-pixy-blue/20 bg-pixy-sky px-3 py-1 text-xs font-semibold text-pixy-blue"
            >
              Utiliser {suggestedPseudo}
            </button>
          ) : null}
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-semibold text-slate-700">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="ton@email.com"
            className={fieldClassName}
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="block text-sm font-semibold text-slate-700">
            Mot de passe
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            placeholder="Minimum 8 caracteres"
            className={fieldClassName}
          />
        </div>
        <div className="space-y-1">
          <label className="block text-sm font-semibold text-slate-700">
            Confirmer le mot de passe
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            placeholder="Retape ton mot de passe"
            className={fieldClassName}
          />
        </div>
      </div>

      {errorMessage && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {errorMessage}
        </div>
      )}

      <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
        <input
          type="checkbox"
          name="acceptTerms"
          checked={formData.acceptTerms}
          onChange={handleChange}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-pixy-blue focus:ring-pixy-blue/20"
          required
        />
        <span>
          J'accepte la{" "}
          <a
            href="/politique-confidentialite"
            className="font-semibold text-pixy-blue underline"
          >
            politique de confidentialité
          </a>
          .
        </span>
      </label>

      <button
        type="submit"
        className="w-full rounded-2xl bg-gradient-to-r from-pixy-green to-pixy-blue px-4 py-3.5 text-sm font-bold uppercase tracking-[0.22em] text-white shadow-md shadow-sky-300/30 transition hover:-translate-y-0.5 hover:shadow-sky-300/50"
      >
        Créer mon compte
      </button>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
        Déjà un compte ?{" "}
        <button
          type="button"
          onClick={() => onSwitch("login")}
          className="font-semibold text-pixy-blue transition hover:text-pixy-green"
        >
          Se connecter
        </button>
      </div>
    </form>
  );
}
